import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  requestDepositStatusV2,
  requestDepositV2,
} from '../../../adapters/payments'
import { recordGaData } from '../../../adapters/tracking'
import { getNetDeposits } from '../../../adapters/transactions'
import { getUserWallet } from '../../../adapters/user'
import { EnvConfig } from '../../../config/EnvConfig'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { useModalContext } from '../../../context/modalProvider'
import { useTranslation } from '../../../context/translationProvider'
import { removeCpfMask } from '../../../helpers/mask'
import { msToMinutesAndSeconds } from '../../../helpers/msToMinutesAndSeconds'
import { queryStringToObject } from '../../../helpers/stringHelper'
import useNavbarHeight from '../../../hooks/useNavbarHeight'
import { useSessionStorage } from '../../../hooks/useSessionStorage'
import LimitErrorIcon from '../../../images/icon_limit_reached_error.svg'
import storageService from '../../../services/storageService'
import {
  INGAME_CTA_ACTIONS,
  INGAME_DRAWERS,
  ModalTypes,
  PaymentStatus,
} from '../../../utils/constants'
import { cookiePrefix, getUpdatedToken } from '../../../utils/cookies'
import { pushDataLayer } from '../../../utils/pushDataLayer'
import { Button } from '../../atoms/button'
import { If } from '../../atoms/if'
import { toast } from '../../atoms/toast'
import { DepositAmountSection } from '../depositAmountSection'
import { DepositPaymentSection } from '../depositPaymentSection'
import { DepositContentContainer } from './styles'

const CHECK_STATUS_INTERVAL = 30000

const DEPOSIT_STEPS = {
  SELECT_AMOUNT: 0,
  REVIEW_PAYMENT: 1,
}

export const DepositContent = ({
  user,
  depositLobbyData,
  depositClose,
  isMobile,
  initialData,
  isPage,
  usableBankAccounts,
  isInGame,
}) => {
  const { open, close } = useModalContext()
  const {
    formState: { errors, isSubmitting },
    setValue,
    control,
    watch,
    trigger,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
  })
  const { translate } = useTranslation()
  const depositAmount = watch('depositAmount')
  const methodId = watch('methodId')
  const navbarHeight = useNavbarHeight()

  const { getSessionValue, setSessionValue, removeSessionValue } =
    useSessionStorage()
  const { trackEvent, getTrackingContext, EVENT_NAMES, EVENT_LOCATIONS } =
    useEventTracking()

  const [depositStep, setDepositStep] = useState(DEPOSIT_STEPS.SELECT_AMOUNT)
  const [requestedDepositData, setRequestedDepositData] = useState(null)

  const amountOptions = [...new Set(depositLobbyData?.defaultAmounts)]
  const isFirstDeposit = depositLobbyData?.firstDeposit
  const isSelectAmountStep = depositStep === DEPOSIT_STEPS.SELECT_AMOUNT
  const currencyCode = user?.wallet?.currency?.short_code || ''
  const currencySymbol = user?.wallet?.currency?.symbol || ''
  const method = depositLobbyData?.methods?.find(
    (method) => method?.paymentMethodDetailId === methodId,
  )

  const maximumAmount = method?.maximumAmount
  const minimumAmount = method?.minimumAmount
  const code = method?.paymentMethodCode.toUpperCase()

  const isDepositDisabled = (() => {
    if (isSubmitting || depositAmount <= 0) return true
    if (errors?.depositAmount) return true
    if (usableBankAccounts?.length <= 0) return true

    return false
  })()

  const handleSetDepositAmount = (value) => {
    setValue('depositAmount', value)
    trigger('depositAmount')
  }

  const handleSetSelectedMethod = (value) => {
    setValue('methodId', value)
  }

  const triggerSpentTimeGTMEvent = () => {
    const registrationStartTime = getSessionValue(
      `${cookiePrefix}:registration_start`,
    )
    const depositFinishTime = new Date().getTime()

    if (registrationStartTime) {
      const fromRegisterTimeSpent = msToMinutesAndSeconds(
        depositFinishTime - registrationStartTime,
      )

      pushDataLayer({
        eventName: 'registration_to_deposit_time_spent',
        data: {
          totalTime: fromRegisterTimeSpent,
        },
      })

      pushDataLayer({
        eventName: 'successful_deposit_after_registration',
        data: {
          totalTime: fromRegisterTimeSpent,
        },
      })
      removeSessionValue(`${cookiePrefix}:registration_start`)
    }

    const depositStartTime = getSessionValue(`${cookiePrefix}:deposit_start`)
    const depositTimeSpent = msToMinutesAndSeconds(
      depositFinishTime - depositStartTime,
    )

    if (isFirstDeposit) {
      window?.dataLayer?.push({
        event: 'new_registration_deposit_time_spent',
        totalTime: depositTimeSpent,
      })
    } else {
      window?.dataLayer?.push({
        event: 'non_new_registration_deposit_time_spent',
        totalTime: depositTimeSpent,
      })
    }

    removeSessionValue(`${cookiePrefix}:deposit_start`)
  }

  const triggerGTMEvent = async ({ code }) => {
    const storedUser = storageService.getUser()
    const { short_code: currency = '' } = storedUser?.wallet?.currency || {}
    const {
      data: { total_num_of_deposits },
    } = await getNetDeposits({
      dateFrom: storedUser?.created_at,
      dateTo: new Date().toISOString().slice(0, 19),
      status: 'OK',
    })

    const alreadyDeposited = total_num_of_deposits > 1

    window?.dataLayer?.push({ ecommerce: null })

    if (alreadyDeposited) {
      window?.dataLayer?.push({
        event: 'normalDeposit',
        user_id: storedUser?.id,
        ecommerce: {
          transaction_id: `T_${storedUser?.id}_${new Date().getTime()}`,
          affiliation: `${EnvConfig.GATSBY_GTM_DOMAIN} Deposit`,
          value: depositAmount,
          tax: 0,
          shipping: 0,
          currency,
          coupon: '',
          items: [
            {
              item_id: `${code}`,
              item_name: `${code}`,
              affiliation: `${EnvConfig.GATSBY_GTM_DOMAIN} Deposit`,
              coupon: '',
              currency,
              discount: 0,
              index: 0,
              item_brand: `${code}`,
              item_category: `${code}`,
              item_category2: `${code}`,
              item_category3: `${code}`,
              item_category4: `${code}`,
              item_category5: `${code}`,
              item_list_id: 'deposit_method',
              item_list_name: 'Deposit Method',
              item_variant: '',
              location_id: '',
              price: depositAmount,
              quantity: 1,
            },
          ],
        },
      })
    } else {
      window?.dataLayer?.push({
        event: 'FirstDeposit',
        user_id: storedUser?.id,
        ecommerce: {
          transaction_id: `T_${storedUser?.id}_${new Date().getTime()}`,
          affiliation: `${EnvConfig.GATSBY_GTM_DOMAIN} Deposit`,
          value: depositAmount,
          tax: 0,
          shipping: 0,
          currency,
          coupon: '',
          items: [
            {
              item_id: `${code}`,
              item_name: `${code}`,
              affiliation: `${EnvConfig.GATSBY_GTM_DOMAIN} Deposit`,
              coupon: '',
              currency,
              discount: 0,
              index: 0,
              item_brand: `${code}`,
              item_category: `${code}`,
              item_category2: `${code}`,
              item_category3: `${code}`,
              item_category4: `${code}`,
              item_category5: `${code}`,
              item_list_id: 'deposit_method',
              item_list_name: 'Deposit Method',
              item_variant: '',
              location_id: '',
              price: depositAmount,
              quantity: 1,
            },
          ],
        },
      })
    }
  }

  const trackDepositCompletedEvent = (responseData) => {
    try {
      const { transactionId } = responseData
      trackEvent(
        EVENT_NAMES.DEPOSIT_COMPLETED,
        {
          user_id: user?.id,
          transaction_id: transactionId,
          amount: depositAmount,
          account_balance: user?.wallet?.amount,
          deposit_method: method?.paymentMethodCode,
        },
        true,
      )
    } catch (err) {
      console.error(err)
    }
  }

  const checkStatus = async (apiCall, reference) => {
    const response = await apiCall(reference)
    const statuses = {
      success: 'OK',
      declined: 'DECLINED',
      pending: 'PENDING',
      cancelled: 'CANCELLED',
    }

    const { transactionStatus, declinedStatusCode, status } =
      response?.data || {}

    if (transactionStatus?.toUpperCase() === statuses.success) {
      triggerSpentTimeGTMEvent()
      await triggerGTMEvent({ code })
      toast.success(translate('payments.deposit.transactionCompleted'))
      window.postMessage('transactionCompleted', '*')
      trackDepositCompletedEvent(response?.data)
      close()
      depositClose()

      return
    }

    if (transactionStatus?.toUpperCase() === statuses.declined) {
      if (status?.toUpperCase() === statuses.cancelled) {
        switch (declinedStatusCode) {
          case '10051':
            toast.error(translate('payments.deposit.cancelled.10051'))
            break
          case '10067':
            toast.error(translate('payments.deposit.cancelled.10067'))
            break
          default:
            toast.error(translate('payments.deposit.cancelled.default'))
            break
        }

        close()

        return
      }

      triggerSpentTimeGTMEvent()
      await triggerGTMEvent({ code })
      toast.error(translate('payments.deposit.generalError'))
      close()

      return
    }

    setTimeout(() => checkStatus(apiCall, reference), CHECK_STATUS_INTERVAL)
  }

  const handleSelectMinimumAmount = (minimum) => {
    const orderedAmounts =
      [...(depositLobbyData?.defaultAmounts || [])].sort((a, b) => a - b) || []
    const firstValidAmount =
      orderedAmounts?.find((amount) => amount >= minimum) || null

    handleSetDepositAmount(firstValidAmount)
  }

  const requestDynamicDepositStatus = async (reference) => {
    return requestDepositStatusV2({
      endpoint: method.statusApiUrl,
      transactionReference: reference,
    })
  }

  const errorMessages = {
    147: translate('payments.depositLimitExceeded'),
    52: translate('payments.statusNotAllowed'),
    460: translate('account.paymentMethodsNotAvailable').replace(
      '{0}',
      user?.wallet?.currency?.symbol || user.wallet.currency.short_code,
    ),
    212: translate('payments.error.CPFrequired'),
    502: translate('payments.error.connectionIssue'),
    210602: translate('payments.error.exceededDepositLimit'),
    24: translate('payments.error.invalidBankAccountDetails'),
  }

  const handleError = (messageCode) => {
    const message =
      errorMessages[messageCode] || translate('payments.deposit.generalError')

    toast.error(message)
  }

  const onSubmit = async (data) => {
    const shouldTrack = isInGame && isSelectAmountStep
    if (shouldTrack) {
      const isCustomInput = !amountOptions.includes(depositAmount)

      trackEvent(
        EVENT_NAMES.INGAME_ACTION_TAKEN,
        {
          drawer: INGAME_DRAWERS.AMOUNT_INPUT,
          cta: INGAME_CTA_ACTIONS.CONTINUE_TO_PAYMENT,
          amount: depositAmount,
          is_custom_input: isCustomInput,
          site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
        },
        true,
      )
    }

    recordGaData({
      ga_cookie: getUpdatedToken('_ga'),
      ga_cookie_session: getUpdatedToken(EnvConfig.GATSBY_GA_COOKIE),
    })

    try {
      let response = null
      let paymentRequested = false

      paymentRequested = true
      response = await requestDepositV2({
        endpoint: method.transactionRequestUrl,
        body: {
          amount: depositAmount,
          nationalId:
            data.bankCpf ||
            data?.documentId ||
            removeCpfMask(data?.national_id),
          paymentMethodDetailId: method?.paymentMethodDetailId,
        },
      })

      if (!paymentRequested) {
        toast.error('Payment not supported')
      }

      if (response) {
        if (response.ok) {
          const { payment_method, url: redirectionURL } = response.data

          getUserWallet()
          // const paymentMethod = getPaymentMethodByCode(code)

          setRequestedDepositData(response.data)
          setDepositStep(DEPOSIT_STEPS.REVIEW_PAYMENT)

          if (isInGame) {
            trackEvent(
              EVENT_NAMES.INGAME_DRAWER_OPENED,
              {
                drawer: INGAME_DRAWERS.CONFIRM_PAYMENT,
                site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
                ...getTrackingContext(),
              },
              true,
            )
          }
          checkStatus(requestDynamicDepositStatus, response.data.reference)

          if (!payment_method && redirectionURL) {
            window.location = redirectionURL
          }
        }

        if (!response.ok) {
          const dataMessageCode = response.error.data?.errors?.[0]?.code
          const messageCode = dataMessageCode ?? response.error?.code

          if (messageCode === 506) {
            open({
              type: ModalTypes.SIMPLE,
              title: translate('common.deposit'),
              data: {
                img: LimitErrorIcon,
                subtitle: translate(
                  'payments.error.exceededDepositLimitOfProvider.title',
                ),
                text: translate(
                  'payments.error.exceededDepositLimitOfProvider.text',
                ),
                buttonText: translate(
                  'payments.error.exceededDepositLimitOfProvider.button',
                ),
              },
            })
          } else {
            handleError(messageCode)
          }
        }

        paymentRequested = false
      }
    } catch (exc) {
      console.error(exc)
    }
  }

  const handleChangeInformation = () => {
    if (isInGame) {
      trackEvent(
        EVENT_NAMES.INGAME_ACTION_TAKEN,
        {
          drawer: INGAME_DRAWERS.CONFIRM_PAYMENT,
          cta: INGAME_CTA_ACTIONS.CHANGE_AMOUNT,
          site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
        },
        true,
      )
    }
    setDepositStep(DEPOSIT_STEPS.SELECT_AMOUNT)
    setRequestedDepositData(null)
  }

  useEffect(() => {
    setSessionValue(`${cookiePrefix}:deposit_start`, new Date().getTime())
    if (!window) return

    window?.dataLayer?.push({
      event: 'Deposit',
    })

    window?.dataLayer?.push({
      event: 'deposits_page_viewed',
    })

    trackEvent(
      EVENT_NAMES.DEPOSIT_AMOUNT_LOADED,
      {
        site_location: isInGame ? EVENT_LOCATIONS.INGAME_NAVBAR : null,
        ...getTrackingContext(),
      },
      true,
    )

    return () => {
      removeSessionValue(`${cookiePrefix}:deposit_start`)
    }
  }, [isInGame])

  useEffect(() => {
    const onPaymentFailed = async (e) => {
      try {
        if (typeof e.data === 'string') {
          const data = JSON.parse(e.data)
          if (data && data.action === PaymentStatus.ERROR) {
            toast.error(translate('payments.receipt.error'))
          }

          if (data && data.action === PaymentStatus.CANCELLED) {
            switch (data.declinedStatusCode) {
              case 10051:
                toast.error(translate('payments.deposit.cancelled.10051'))
                break
              case 10067:
                toast.error(translate('payments.deposit.cancelled.10067'))
                break
              default:
                toast.error(translate('payments.deposit.cancelled.default'))
                break
            }
          }

          if (data && data.action === PaymentStatus.SUCCESS) {
            toast.success(translate('payments.deposit.success'))
            depositClose()
            await triggerGTMEvent({ code })
            triggerSpentTimeGTMEvent()

            open({
              title: translate('payments.receipt.deposit'),
              data: data,
              type: ModalTypes.RECEIPT,
            })
          }
        }
      } catch (ex) {
        console.error('error occured', ex)
      }
    }

    window.addEventListener('message', onPaymentFailed)

    return () => {
      window.removeEventListener('message', onPaymentFailed)
    }
  }, [])

  useEffect(() => {
    let firstMinimumAmount = 0
    const urlParams = queryStringToObject(window?.location?.search)

    if (urlParams?.action === 'deposit' && urlParams?.method) {
      const method = depositLobbyData?.methods?.find(
        (method) => method?.paymentMethodCode === urlParams?.method,
      )
      if (method) handleSetSelectedMethod(method.paymentMethodDetailId)
    } else if (initialData?.method) {
      const { paymentMethodDetailId, minimumAmount: firstMinimum } =
        depositLobbyData?.methods?.find(
          (method) => method?.paymentMethodCode === initialData.method,
        ) || {}

      if (!paymentMethodDetailId) {
        return
      }

      firstMinimumAmount = firstMinimum
      setValue('methodId', paymentMethodDetailId)
    } else {
      setValue('methodId', depositLobbyData?.methods[0]?.paymentMethodDetailId)
      firstMinimumAmount = depositLobbyData?.methods[0]?.minimumAmount
    }

    if (urlParams?.action === 'deposit' && urlParams?.amount) {
      handleSetDepositAmount(urlParams?.amount)
    } else if (initialData?.amount) {
      handleSetDepositAmount(initialData.amount)
    } else {
      handleSelectMinimumAmount(firstMinimumAmount)
    }
  }, [initialData])

  useEffect(() => {
    trigger('depositAmount')
  }, [methodId])

  useEffect(() => {
    const shouldTrack = isInGame && isSelectAmountStep
    if (!shouldTrack) {
      return
    }
    trackEvent(
      EVENT_NAMES.INGAME_DRAWER_OPENED,
      {
        drawer: INGAME_DRAWERS.AMOUNT_INPUT,
        site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
        ...getTrackingContext(),
      },
      true,
    )
  }, [isSelectAmountStep, isInGame])

  return (
    <>
      <DepositContentContainer
        isMobile={isMobile}
        onSubmit={handleSubmit(onSubmit)}
        navbarHeight={navbarHeight}
        isInGame={isInGame}
      >
        <If
          condition={isSelectAmountStep}
          render={() => (
            <>
              <DepositAmountSection
                control={control}
                errors={errors}
                depositAmount={depositAmount}
                handleSetDepositAmount={handleSetDepositAmount}
                amountOptions={amountOptions}
                minimumAmount={minimumAmount}
                maximumAmount={maximumAmount}
                currencySymbol={currencySymbol}
                currencyCode={currencyCode}
                isPage={isPage}
                translate={translate}
              />

              <Button
                expand
                disabled={isDepositDisabled}
                id='btnDepositModal'
                loading={isSubmitting}
              >
                {translate('payments.depositDrawer.continueButton')}
              </Button>
            </>
          )}
        />

        <If
          condition={depositStep === DEPOSIT_STEPS.REVIEW_PAYMENT}
          render={() => (
            <DepositPaymentSection
              requestedDepositData={requestedDepositData}
              currencySymbol={currencySymbol}
              handleChange={handleChangeInformation}
              banks={usableBankAccounts}
              isMobile={isMobile}
              isInGame={isInGame}
              userId={user?.id}
            />
          )}
        />
      </DepositContentContainer>
    </>
  )
}

DepositContent.propTypes = {
  user: PropTypes.shape({
    created_at: PropTypes.string,
    id: PropTypes.string,
    national_id: PropTypes.string,
    wallet: PropTypes.object,
  }),
  depositClose: PropTypes.func,
  depositLobbyCMSData: PropTypes.shape({
    headerText: PropTypes.string,
    offerSection: PropTypes.shape({
      description: PropTypes.string,
      icon: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
  depositLobbyData: PropTypes.shape({
    defaultAmounts: PropTypes.arrayOf(PropTypes.number),
    firstDeposit: PropTypes.bool,
    methods: PropTypes.arrayOf(
      PropTypes.shape({
        maximumAmount: PropTypes.number,
        minimumAmount: PropTypes.number,
        paymentMethodCode: PropTypes.string,
        paymentMethodDetailId: PropTypes.string,
      }),
    ),
  }),
  initialData: PropTypes.shape({
    amount: PropTypes.number,
    bonus: PropTypes.string,
    method: PropTypes.string,
  }),
  isPage: PropTypes.bool,
  isMobile: PropTypes.bool,
  usableBankAccounts: PropTypes.arrayOf(PropTypes.object),
  isInGame: PropTypes.bool,
}
